'use client'

import { useState, useRef, useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { usePathname } from 'next/navigation';
import { routing, type Locale } from '@/i18n/routing';
import US from 'country-flag-icons/react/3x2/US';
import ES from 'country-flag-icons/react/3x2/ES';
import BR from 'country-flag-icons/react/3x2/BR';
import './LanguageSwitcher.css';

// Flag components mapped to locales
const localeFlags: Record<Locale, React.ComponentType<{ title?: string; className?: string }>> = {
  en: US,
  es: ES,
  pt: BR
};

// Short labels for compact display
const localeLabels: Record<Locale, string> = {
  en: 'EN',
  es: 'ES',
  pt: 'PT',
};

interface LanguageSwitcherProps {
  variant?: 'buttons' | 'dropdown'
}

export default function LanguageSwitcher({ variant = 'buttons' }: LanguageSwitcherProps) {
  const locale = useLocale() as Locale;
  const t = useTranslations('languageSwitcher');
  const pathname = usePathname();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Detect if we're in i18n context (routes with /en/, /es/, /pt/)
  const isI18nRoute = pathname?.match(/^\/(en|es|pt)(\/|$)/) !== null;

  // Handler for non-i18n routes (cookie-based locale switching)
  const handleLocaleChange = (newLocale: Locale) => {
    if (isI18nRoute) return; // Let Link handle it
    document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
    window.location.reload();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // ── DROPDOWN VARIANT (desktop navbar) ──────────────────────────────
  if (variant === 'dropdown') {
    const CurrentFlag = localeFlags[locale];
    const pathWithoutLocale = pathname?.replace(/^\/(en|es|pt)/, '') || '/';

    return (
      <div className="lang-dropdown-wrapper" ref={wrapperRef}>
        <button
          className={`lang-dropdown-trigger${dropdownOpen ? ' open' : ''}`}
          onClick={() => setDropdownOpen(prev => !prev)}
          aria-expanded={dropdownOpen}
          aria-haspopup="listbox"
          aria-label={`${t('selectLanguage')}: ${t(locale)}`}
          type="button"
        >
          <span className="lang-flag">
            <CurrentFlag title={t(locale)} className="flag-icon" />
          </span>
          <span className="lang-label">{localeLabels[locale]}</span>
          <svg
            className={`lang-dropdown-chevron${dropdownOpen ? ' open' : ''}`}
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {dropdownOpen && (
          <div className="lang-dropdown-panel" role="listbox">
            {routing.locales.map((loc) => {
              const FlagIcon = localeFlags[loc];
              const isActive = locale === loc;

              if (isI18nRoute) {
                return (
                  <Link
                    key={loc}
                    href={pathWithoutLocale}
                    locale={loc}
                    className={`lang-dropdown-option${isActive ? ' active' : ''}`}
                    aria-selected={isActive}
                    onClick={() => setDropdownOpen(false)}
                    title={t(loc)}
                  >
                    <span className="lang-flag">
                      <FlagIcon title={t(loc)} className="flag-icon" />
                    </span>
                    <span className="lang-option-name">{t(loc)}</span>
                    {isActive && (
                      <svg className="lang-check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7L5.5 10.5L12 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </Link>
                );
              }

              return (
                <button
                  key={loc}
                  onClick={() => { setDropdownOpen(false); handleLocaleChange(loc); }}
                  className={`lang-dropdown-option${isActive ? ' active' : ''}`}
                  aria-selected={isActive}
                  title={t(loc)}
                  type="button"
                >
                  <span className="lang-flag">
                    <FlagIcon title={t(loc)} className="flag-icon" />
                  </span>
                  <span className="lang-option-name">{t(loc)}</span>
                  {isActive && (
                    <svg className="lang-check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7L5.5 10.5L12 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── BUTTONS VARIANT (default — mobile menu) ─────────────────────────
  return (
    <div
      className="language-switcher"
      role="group"
      aria-label={t('selectLanguage')}
    >
      {routing.locales.map((loc) => {
        const FlagIcon = localeFlags[loc];
        const isActive = locale === loc;

        // For i18n routes: use Link component with locale switching
        if (isI18nRoute) {
          // Extract the path without locale prefix
          const pathWithoutLocale = pathname?.replace(/^\/(en|es|pt)/, '') || '/';

          return (
            <Link
              key={loc}
              href={pathWithoutLocale}
              locale={loc}
              className={`lang-btn ${isActive ? 'active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              aria-label={`${t('selectLanguage')}: ${t(loc)}`}
              title={t(loc)}
            >
              <span className="lang-flag">
                <FlagIcon title={t(loc)} className="flag-icon" />
              </span>
              <span className="lang-label">{localeLabels[loc]}</span>
            </Link>
          );
        }

        // For non-i18n routes: use button with onClick
        return (
          <button
            key={loc}
            onClick={() => handleLocaleChange(loc)}
            className={`lang-btn ${isActive ? 'active' : ''}`}
            aria-current={isActive ? 'page' : undefined}
            aria-label={`${t('selectLanguage')}: ${t(loc)}`}
            title={t(loc)}
            type="button"
          >
            <span className="lang-flag">
              <FlagIcon title={t(loc)} className="flag-icon" />
            </span>
            <span className="lang-label">{localeLabels[loc]}</span>
          </button>
        );
      })}
    </div>
  );
}
